import type { ProjectHistoryEventKind } from "@/api/hooks";

/**
 * The history vocabulary's ONE table home (#438), on `projectStatus.ts`'s
 * discipline: exhaustive switches whose `never` default fails the build when
 * #432's contract enum grows, plus the runtime half — a member this build has
 * never heard of renders as itself, visibly odd rather than silently blank
 * (the contract's own instruction: "a client must tolerate unknown members
 * and render them safely rather than guess").
 *
 * ## Nothing here is derived
 *
 * Each function maps ONE projected value to ONE string. The event kind and
 * the outcome word both arrive verbatim from immutable evidence rows; no
 * label is composed from a second field, no event is invented, and no
 * ordering is re-derived — the projection is newest-first and the surface
 * renders it as served.
 */

/** The event, in a clinician's words. */
export function historyEventLabel(kind: ProjectHistoryEventKind): string {
  switch (kind) {
    case "project_created":
      return "Project created";
    case "preview_target_changed":
      return "Preview target changed";
    case "revision_frozen":
      return "Test version frozen";
    case "preview_load":
      return "Preview";
    case "candidate_accepted":
      return "Version accepted after preview";
    case "publication_requested":
      return "Sent for review";
    case "trial_run_completed":
      // The provisional nature rides the LABEL, not only the technical
      // stamp: a trial output must never read as evidence, even compactly.
      return "Trial run completed (provisional)";
    case "promotion_handoff_created":
      return "Handed to the release team";
    case "project_archived":
      return "Project archived";
    case "project_restored":
      return "Project restored";
    default:
      return unrecognised(kind);
  }
}

/**
 * The preview load's outcome vocabulary, softened for the compact line. The
 * members are `candidate_load_events.outcome`'s CHECK vocabulary, and only
 * this kind gets a map: every other kind's outcome word is either implied by
 * its label (an archive row's action) or already clinician-safe, and renders
 * verbatim — never hidden, per the fail-visible rule above.
 */
const PREVIEW_LOAD_OUTCOME: Record<string, string> = {
  served: "loaded",
  resolved: "opened as a patient",
  refused_expired: "refused — the version had expired",
  refused_not_eligible: "refused — the version was not eligible",
  failed: "failed",
};

/**
 * The concise outcome for one event, or null when the label already says it
 * all. Unknown outcome words render verbatim.
 */
export function historyOutcomeLabel(
  kind: ProjectHistoryEventKind,
  outcome: string | null | undefined,
): string | null {
  if (outcome === null || outcome === undefined || outcome === "") {
    return null;
  }
  if (kind === "preview_load") {
    return PREVIEW_LOAD_OUTCOME[outcome] ?? outcome;
  }
  // Archive/restore rows carry their source action (`archive` / `restore`) as
  // the outcome, and the label already states it. Suppress those exact
  // server-recorded words rather than testing friendlier spellings the API
  // never sends.
  if (
    (kind === "project_archived" && outcome === "archive") ||
    (kind === "project_restored" && outcome === "restore")
  ) {
    return null;
  }
  return outcome;
}

/**
 * The safe actor phrase. An absent/redacted subject is stated explicitly but
 * never guessed, as #438 requires; the raw opaque subject remains confined to
 * Technical details. "you" is the one attribution a browser can make truthfully
 * (`CandidateAcceptanceDetail`'s rule); every other non-redacted subject is
 * "another account". That wording covers both people and service principals
 * (preview-load evidence is commonly written by Brain) without guessing which
 * kind of principal an opaque subject represents.
 */
export function historyActorPhrase(
  actor: string | null | undefined,
  viewerSubject: string,
): string {
  if (actor === null || actor === undefined || actor === "") {
    return "actor unavailable";
  }
  return actor === viewerSubject ? "by you" : "by another account";
}

/**
 * A compact but truthful candidate identity. Generated ids are long, so their
 * final eight characters connect related rows without dominating the line.
 * Legacy ids may be eight characters or shorter; those render in full because
 * an ellipsis would falsely claim that characters had been omitted.
 */
export function historyCandidateIdentity(candidateId: string): string {
  return candidateId.length <= 8 ? candidateId : `…${candidateId.slice(-8)}`;
}

/** See `projectStatus.unrecognised` — the same runtime fail-visible arm. */
function unrecognised(member: never): string {
  return String(member);
}
